import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  Stack,
  useTheme,
  alpha,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Grid,
  styled,
  Tooltip,
  CardContent,
  Alert
} from "@mui/material";

import { 
  Delete, 
  Edit, 
  Add, 
  Refresh,
  Save,
  ChevronLeft,
  ChevronRight,
  Search,
  Clear,
  Close,
  Upload,
  GetApp,
  Download,
  CloudUpload,
  PictureAsPdf
  
} from "@mui/icons-material";

import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import { usePageTitle } from "contexts/PageTitleContext";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import AppPagination from "components/AppPagination";

// ✅ ADDED: Import button config for role-based permissions
import buttonConfig from "../../config/ConfigAccessButton";

// Styled components matching Permission page
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.02),
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
    transition: 'background-color 0.2s ease-in-out',
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

// Scrollbar styled component
const ScrollbarStyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 2,
  overflow: "auto",
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.08)',
  
  /* Custom scrollbar styles */
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.05),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.common.black, 0.2),
    borderRadius: '4px',
    transition: 'background 0.2s ease-in-out',
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.common.white, 0.3)
        : alpha(theme.palette.common.black, 0.3),
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  
  /* Firefox scrollbar */
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.mode === 'dark'
    ? `${alpha(theme.palette.common.white, 0.2)} ${alpha(theme.palette.common.white, 0.05)}`
    : `${alpha(theme.palette.common.black, 0.2)} ${alpha(theme.palette.common.black, 0.05)}`,
}));

// Styled DialogContent with scrollbar
const ScrollbarStyledDialogContent = styled(DialogContent)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.05),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.common.black, 0.2),
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.common.white, 0.3)
        : alpha(theme.palette.common.black, 0.3),
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.mode === 'dark'
    ? `${alpha(theme.palette.common.white, 0.2)} ${alpha(theme.palette.common.white, 0.05)}`
    : `${alpha(theme.palette.common.black, 0.2)} ${alpha(theme.palette.common.black, 0.05)}`,
}));

// Upload Area styled component for FAQ Upload
const UploadArea = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  }
}));

const FAQ = () => {
  const theme = useTheme();
  const toast = useToast();
  const toastRef = useRef(toast);
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  // ✅ ADDED: Permission helper
  const faqConfig = buttonConfig["FAQ"] || {};
  const can = (action) => !!faqConfig[action];

  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // FAQ Upload states - Combined Template Download and Import
  const [faqUploadDialogOpen, setFaqUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category_id: ""
  });

  // Search and filter states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    page_size: 10,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  // Get auth token from localStorage with multiple key attempts
  const getAuthToken = () => {
    const possibleKeys = [
      'access_token',
      'token',
      'auth_token',
      'accessToken',
      'Authorization',
      'bearer_token'
    ];
    
    for (const key of possibleKeys) {
      const token = localStorage.getItem(key);
      if (token && token !== 'undefined' && token !== 'null' && token !== '') {
        console.log(`Token found with key: ${key}`);
        return token;
      }
    }
    
    for (const key of possibleKeys) {
      const token = sessionStorage.getItem(key);
      if (token && token !== 'undefined' && token !== 'null' && token !== '') {
        console.log(`Token found in sessionStorage with key: ${key}`);
        return token;
      }
    }
    
    console.error("No authentication token found in any storage");
    return null;
  };

  const debugLocalStorage = () => {
    console.log("=== Available localStorage keys ===");
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      console.log(`${key}: ${value ? (value.length > 50 ? value.substring(0, 50) + "..." : value) : "Empty"}`);
    }
    console.log("=== End of localStorage keys ===");
  };

  useEffect(() => {
    setPageTitle("FAQ Management");
    debugLocalStorage();
  }, [setPageTitle]);

  /* ----------------------------------------- */
  /* FETCH CATEGORIES                           */
  /* ----------------------------------------- */

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      console.log("Fetching categories from:", API_ENDPOINTS.GET_categories_for_faq);
      const response = await apiService.get(API_ENDPOINTS.GET_categories_for_faq);
      console.log("Categories API Response:", response);
      
      if (response?.success) {
        let categoriesData = [];
        
        if (response.data && Array.isArray(response.data)) {
          categoriesData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          categoriesData = response.data.data;
        } else if (response.data && response.data.categories && Array.isArray(response.data.categories)) {
          categoriesData = response.data.categories;
        } else if (response.data && typeof response.data === 'object') {
          categoriesData = Object.values(response.data).filter(item => item && item.id && item.name);
        }
        
        console.log("Processed Categories:", categoriesData);
        setCategories(categoriesData);
      } else {
        console.log("No categories found or invalid response");
        setCategories([]);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /* ----------------------------------------- */
  /* FETCH FAQS                                 */
  /* ----------------------------------------- */

  const fetchFaqs = useCallback(async (page = 1, size = 10) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toastRef.current?.showError('No internet connection-offline mode active.');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      let url;
      let response;
      
      if (selectedCategory) {
        url = `${API_ENDPOINTS.GET_faq_by_category}?category=${selectedCategory}`;
        if (page) url += `&page=${page}`;
        if (size) url += `&page_size=${size}`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        
        console.log("Fetching FAQs by category from:", url);
        response = await apiService.get(url);
      } else {
        url = `${API_ENDPOINTS.GET_faq}?page=${page}&page_size=${size}`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        
        console.log("Fetching all FAQs from:", url);
        response = await apiService.get(url);
      }

      console.log("FAQs API Response:", response);

      if (response?.success) {
        let faqsList = [];
        let totalItems = 0;
        let totalPages = 1;

        if (response.data && Array.isArray(response.data)) {
          faqsList = response.data;
          totalItems = response.data.length;
          totalPages = Math.ceil(totalItems / size);
        } else if (response.data && response.data.FAQs && Array.isArray(response.data.FAQs)) {
          faqsList = response.data.FAQs;
          totalItems = response.data.total || response.data.FAQs.length;
          totalPages = response.data.total_pages || Math.ceil(totalItems / size);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          faqsList = response.data.data;
          totalItems = response.data.total || response.data.data.length;
          totalPages = response.data.total_pages || Math.ceil(totalItems / size);
        } else if (response.data && response.data.items && Array.isArray(response.data.items)) {
          faqsList = response.data.items;
          totalItems = response.data.total || response.data.items.length;
          totalPages = response.data.total_pages || Math.ceil(totalItems / size);
        } else if (response.data && typeof response.data === 'object') {
          faqsList = Object.values(response.data).filter(item => 
            item && (item.question || item.answer) && typeof item === 'object'
          );
          totalItems = faqsList.length;
          totalPages = Math.ceil(totalItems / size);
        }

        const backendPagination = response.data?.pagination || response.pagination;
        const paginationPayload = backendPagination
          ? {
              current_page: backendPagination.current_page ?? page,
              page_size: backendPagination.page_size ?? size,
              total_items: backendPagination.total_items ?? totalItems,
              total_pages: backendPagination.total_pages ?? totalPages,
              has_next: !!backendPagination.has_next,
              has_previous: !!backendPagination.has_previous,
              next_page_number: backendPagination.next_page_number ?? null,
              previous_page_number: backendPagination.previous_page_number ?? null
            }
          : {
              current_page: page,
              total_pages: totalPages || 1,
              total_items: totalItems || faqsList.length,
              page_size: size,
              has_next: page < (totalPages || 1),
              has_previous: page > 1,
              next_page_number: page < (totalPages || 1) ? page + 1 : null,
              previous_page_number: page > 1 ? page - 1 : null
            };

        console.log("Processed FAQs:", faqsList);
        console.log("Pagination:", paginationPayload);

        setFaqs(faqsList);
        setPagination(paginationPayload);

        if (currentPage !== paginationPayload.current_page) {
          setCurrentPage(paginationPayload.current_page);
        }
        if (pageSize !== paginationPayload.page_size) {
          setPageSize(paginationPayload.page_size);
        }
      } else {
        console.log("No FAQs found or invalid response");
        setFaqs([]);
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          page_size: size,
          has_next: false,
          has_previous: false,
          next_page_number: null,
          previous_page_number: null
        });
      }
    } catch (error) {
      console.error("Failed to load FAQs:", error);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm, currentPage, pageSize]);

  useEffect(() => {
    fetchFaqs(currentPage, pageSize);
  }, [currentPage, pageSize, selectedCategory, searchTerm, fetchFaqs]);

  /* ----------------------------------------- */
  /* SEARCH HANDLERS                           */
  /* ----------------------------------------- */

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
    setHasSearched(true);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  /* ----------------------------------------- */
  /* CATEGORY HANDLER                          */
  /* ----------------------------------------- */

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  /* ----------------------------------------- */
  /* PAGE SIZE HANDLER                         */
  /* ----------------------------------------- */

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchFaqs(1, newSize);
  };

  /* ----------------------------------------- */
  /* PAGINATION HANDLERS                        */
  /* ----------------------------------------- */

  const handlePreviousPage = () => {
    const target = pagination.previous_page_number ?? currentPage - 1;
    if (pagination.has_previous && target >= 1) {
      setCurrentPage(target);
    }
  };

  const handleNextPage = () => {
    const target = pagination.next_page_number ?? currentPage + 1;
    if (pagination.has_next && target <= pagination.total_pages) {
      setCurrentPage(target);
    }
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= pagination.total_pages) {
      setCurrentPage(pageNum);
    }
  };

  const getVisiblePageNumbers = () => {
    const total = pagination.total_pages;
    const current = pagination.current_page;
    if (total <= 4) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const start = Math.max(1, Math.min(current - 1, total - 3));
    const end = Math.min(total, start + 3);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  /* ----------------------------------------- */
  /* DIALOG                                    */
  /* ----------------------------------------- */

  const openDialog = (faq = null) => {
    if (faq) {
      setEditingId(faq.id);
      setFormData({
        question: faq.question || "",
        answer: faq.answer || "",
        category: faq.category || ""
      });
    } else {
      setEditingId(null);
      setFormData({
        question: "",
        answer: "",
        category_id: selectedCategory || ""
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      question: "",
      answer: "",
      category_id: ""
    });
  };

  /* ----------------------------------------- */
  /* FAQ UPLOAD (TEMPLATE DOWNLOAD & IMPORT) HANDLERS */
  /* ----------------------------------------- */

  const openFaqUploadDialog = () => {
    setFaqUploadDialogOpen(true);
    setUploadFile(null);
    setUploadError("");
    setUploadSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const closeFaqUploadDialog = () => {
    setFaqUploadDialogOpen(false);
    setUploadFile(null);
    setUploadError("");
    setUploadSuccess(false);
    setUploading(false);
    setDownloadingTemplate(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setUploadError("");
    
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found. Please login again.");
      }

      console.log("Downloading FAQ template Excel file...");
      console.log("Token found:", token.substring(0, 20) + "...");
      
      const response = await fetch('https://devdashboard.nexgenagritech.ai/api/m2/faq/download-faq-template/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*'
        }
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`Download failed with status: ${response.status}`);
      }

      const blob = await response.blob();
      console.log("Blob size:", blob.size, "Blob type:", blob.type);
      
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }
      
      if (blob.type === 'text/html' || blob.type.includes('html')) {
        const text = await blob.text();
        console.error("Received HTML error:", text.substring(0, 500));
        throw new Error("Server returned HTML error. Please check authentication.");
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'faq_template.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      } else {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        filename = `faq_template_${timestamp}.xlsx`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.showSuccess("Template downloaded successfully");
      
    } catch (error) {
      console.error("Template download failed:", error);
      const errorMessage = error?.message || "Failed to download template. Please try again.";
      setUploadError(errorMessage);
      toast.showError(errorMessage);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.xlsm', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (validExtensions.includes(fileExtension)) {
        setUploadFile(file);
        setUploadError("");
        setUploadSuccess(false);
      } else {
        setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
        event.target.value = null;
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && (file.type.includes('sheet') || file.name.match(/\.(xlsx|xls|xlsm|csv)$/))) {
      setUploadFile(file);
      setUploadError("");
      setUploadSuccess(false);
    } else {
      setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", uploadFile);

      console.log("Uploading FAQ file:", uploadFile.name);
      console.log("File size:", uploadFile.size);

      const token = getAuthToken();
      const response = await apiService.post('m2/faq/import_faqs_from_excel/', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log("FAQ Upload response:", response);

      if (response?.success) {
        setUploadSuccess(true);
        toast.showSuccess(response?.message || "FAQs uploaded successfully");
        setTimeout(() => {
          closeFaqUploadDialog();
          fetchFaqs(currentPage, pageSize);
          fetchCategories();
        }, 2000);
      } else {
        setUploadError(response?.message || "Upload failed. Please try again.");
        toast.showError(response?.message || "Upload failed");
      }
    } catch (error) {
      console.error("FAQ Upload failed:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Upload failed. Please check your file and try again.";
      setUploadError(errorMessage);
      toast.showError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  /* ----------------------------------------- */
  /* SAVE FAQ                                  */
  /* ----------------------------------------- */

  const saveFaq = async () => {
    if (!formData.question || !formData.answer) {
      toast.showError("Please fill in all required fields");
      return;
    }
    
    setSaving(true);

    try {
      let response;
      
      const saveData = {
        question: formData.question,
        answer: formData.answer
      };

      if (formData.category) {
        saveData.category = formData.category;
      }

      if (editingId) {
        saveData.faq_id = editingId;
        console.log("Updating FAQ:", saveData);
        response = await apiService.put(API_ENDPOINTS.UPDATE_feq_new, saveData);
      } else {
        console.log("Creating FAQ:", saveData);
        response = await apiService.post(API_ENDPOINTS.POST_Add_faq, saveData);
      }

      console.log("Save response:", response);

      if (response?.success) {
        toast.showSuccess(editingId ? "FAQ updated successfully" : "FAQ added successfully");
        closeDialog();
        fetchFaqs(currentPage, pageSize);
        fetchCategories();
      } else {
        toast.showError(response?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Save failed:", error);
      toast.showError("Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------------------- */
  /* DELETE FAQ                                */
  /* ----------------------------------------- */

  const deleteFaq = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete FAQ",
      message: "Are you sure you want to delete this FAQ?"
    });

    if (!confirmed) return;

    setDeletingId(id);

    try {
      console.log("Deleting FAQ:", id);
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_faq}?faq_id=${id}`);
      console.log("Delete response:", response);

      if (response?.success) {
        toast.showSuccess("FAQ deleted successfully");
        fetchFaqs(currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.showError("Failed to delete FAQ");
    } finally {
      setDeletingId(null);
    }
  };

  /* ----------------------------------------- */
  /* HELPER FUNCTIONS                          */
  /* ----------------------------------------- */

  const getCategoryName = (categoryId) => {
    if (!categoryId || !categories.length) return '';
    const category = categories.find(c => String(c.id) === String(categoryId));
    return category ? category.name : '';
  };

  /* ----------------------------------------- */
  /* RENDER TABLE ROWS                          */
  /* ----------------------------------------- */

  const renderTableRows = () => {
    if (!faqs || faqs.length === 0) {
      return (
        <TableRow>
          <StyledTableCell colSpan={5} align="center" sx={{ py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No FAQs found
            </Typography>
            {(selectedCategory || searchTerm) && (
              <Button
                variant="text"
                onClick={() => {
                  setSelectedCategory("");
                  setSearchTerm("");
                  setSearchInput("");
                }}
                sx={{ mt: 1 }}
              >
                Clear all filters
              </Button>
            )}
          </StyledTableCell>
        </TableRow>
      );
    }

    return faqs.map((item, index) => {
      const question = item.question || item.question_text || item.title || 'No question';
      const answer = item.answer || item.answer_text || item.description || 'No answer';
      const categoryId = item.category_id || item.categoryId || item.category?.id;
      const categoryName = item.category || item.category?.name || getCategoryName(categoryId);
      const itemId = item.id || item.faq_id || item.ID || index;

      return (
        <StyledTableRow key={itemId}>
          <StyledTableCell sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {(currentPage - 1) * pageSize + index + 1}
            </Typography>
          </StyledTableCell>
          <StyledTableCell sx={{ maxWidth: 300 }}>
            <Typography noWrap title={question} variant="body2">
              {question}
            </Typography>
          </StyledTableCell>
          <StyledTableCell sx={{ maxWidth: 400 }}>
            <Typography noWrap title={answer} variant="body2" color="text.secondary">
              {answer}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            {categoryName ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: 'inline-block'
                }}
              >
                {categoryName}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </StyledTableCell>
          <StyledTableCell align="center">
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">

              {/* ✅ MODIFIED: Edit button with permission */}
              <Tooltip title={can('edit_faq') ? "Edit FAQ" : "No permission to edit FAQ"}>
                <span>
                  <IconButton
                    color="editGreen"
                    onClick={() => can('edit_faq') && openDialog(item)}
                    disabled={!can('edit_faq')}
                    size="small"
                    // sx={{ 
                    //   color: theme.palette.primary.main,
                    //   '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                    // }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* ✅ MODIFIED: Delete button with permission */}
              <Tooltip title={can('delete_faq') ? "Delete FAQ" : "No permission to delete FAQ"}>
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    disabled={deletingId === itemId || !can('delete_faq')}
                    onClick={() => can('delete_faq') && deleteFaq(itemId)}
                    sx={{ '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) } }}
                  >
                    {deletingId === itemId ? (
                      <CircularProgress size={18} color="error" />
                    ) : (
                      <Delete fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

            </Box>
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  /* ----------------------------------------- */
  /* UI                                        */
  /* ----------------------------------------- */

  return (
    <PageContainer>
      {/* Header Card */}
      <HeaderCard>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              label="Filter by Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category, index) => (
                <MenuItem key={index} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search by question or answer..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              if (hasSearched && e.target.value !== searchTerm) {
                setHasSearched(false);
              }
            }}
            onKeyPress={handleKeyPress}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} edge="end">
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            startIcon={<Search />}
            disabled={!searchInput.trim() || (searchInput === searchTerm && hasSearched)}
            sx={{ borderRadius: 2, textTransform: "none", height: 40, flexShrink: 0 }}
          >
            Search
          </Button>

          <FormControl size="small" sx={{ minWidth: 90, flexShrink: 0 }}>
            <InputLabel>Rows</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label="Rows"
            >
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={can('faq_upload') ? "" : "No permission to upload FAQs"}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudUpload />}
                onClick={() => can('faq_upload') && openFaqUploadDialog()}
                disabled={!can('faq_upload')}
                sx={{
                  textTransform: 'none',
                  flexShrink: 0,
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }}
              >
                FAQ Upload
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={can('add_faq') ? "" : "No permission to add FAQ"}>
            <span>
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => can('add_faq') && openDialog()}
                disabled={!can('add_faq')}
                size="small"
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                Add FAQ
              </Button>
            </span>
          </Tooltip>

          <Button
            startIcon={<Refresh />}
            variant="contained"
            onClick={() => {
              fetchFaqs(currentPage, pageSize);
              fetchCategories();
            }}
            disabled={loading}
            size="small"
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>
    
     {/* Table Card */}
      <TableCard>
        <ScrollbarStyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className="header" sx={{ width: '80px', textAlign: 'center' }}>
                  Sr
                </StyledTableCell>
                <StyledTableCell className="header">Question</StyledTableCell>
                <StyledTableCell className="header">Answer</StyledTableCell>
                <StyledTableCell className="header" sx={{ width: 150 }}>Category</StyledTableCell>
                <StyledTableCell className="header" sx={{ width: 120, textAlign: 'center',pl:6}}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                      Loading FAQs...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (
                renderTableRows()
              )}
            </TableBody>
          </Table>
        </ScrollbarStyledTableContainer>

        {/* Pagination */}
        {!loading && faqs.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mt: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {pagination.total_items > 0
                ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items} entries`
                : (faqs.length > 0 ? `Showing ${faqs.length} items` : 'No items')}
              {selectedCategory && ` in ${selectedCategory} category`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {pagination.total_pages > 1 && (
                <AppPagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={(_, pageNum) => handlePageClick(pageNum)}
                />
              )}
            </Box>
          </Box>
        )}
      </TableCard>

      {/* Add/Edit FAQ Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingId ? "Edit FAQ" : "Add New FAQ"}
            </Typography>
            <IconButton onClick={closeDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <ScrollbarStyledDialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={formData.category || ""}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                label="Category"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
              
                {categories.map((category, index) => (
                  <MenuItem key={index} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Question *"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter question..."
            />

            <TextField
              label="Answer *"
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              fullWidth
              multiline
              rows={4}
              placeholder="Enter answer..."
            />
          </Box>
        </ScrollbarStyledDialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          {/* ✅ MODIFIED: Save button respects add_faq / edit_faq permission */}
          <Tooltip title={editingId ? (!can('edit_faq') ? "No permission to edit FAQ" : "") : (!can('add_faq') ? "No permission to add FAQ" : "")}>
            <span>
              <Button
                variant="contained"
                onClick={saveFaq}
                disabled={saving || !formData.question?.trim() || !formData.answer?.trim() || (editingId ? !can('edit_faq') : !can('add_faq'))}
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              >
                {saving ? "Saving..." : (editingId ? "Update" : "Save")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* FAQ Upload Dialog - Combined Template Download and Import */}
      <Dialog open={faqUploadDialogOpen} onClose={closeFaqUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload color="primary" />
            <Typography variant="h6">Bulk Upload FAQs</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Step 1: Format Check */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 1: Check Your Format
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                If you already have data in the correct format with all required columns, you can skip to Step 3. 
                Otherwise, download the template to ensure proper formatting.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Required columns: Question, Answer, Category
              </Typography>
            </Box>

            {/* Download Template Section */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 2: Download Template (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Download the Excel template to ensure your data is in the correct format.
              </Typography>
              <Button
                variant="outlined"
                startIcon={downloadingTemplate ? <CircularProgress size={16} /> : <GetApp />}
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {downloadingTemplate ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Downloading...
                  </>
                ) : (
                  'Download Template'
                )}
              </Button>
            </Box>

            {/* Upload Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 3: Upload Your File
              </Typography>
              
              <UploadArea
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                
                {uploadFile ? (
                  <Box>
                    <Typography variant="h6" color="primary">
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Click to select Excel file or drag & drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supports .xlsx, .xls, .xlsm, and .csv files
                    </Typography>
                  </Box>
                )}
              </UploadArea>
            </Box>

            {/* Error Message */}
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}

            {/* Success Message */}
            {uploadSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                File uploaded successfully! FAQs are being processed.
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={closeFaqUploadDialog} 
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          {/* ✅ MODIFIED: Upload button respects faq_upload permission */}
          <Tooltip title={!can('faq_upload') ? "No permission to upload FAQs" : ""}>
            <span>
              <Button
                onClick={handleBulkUpload}
                variant="contained"
                disabled={!uploadFile || uploading || !can('faq_upload')}
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default FAQ;